import { useAbTesting } from '@packages/abtesting';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { AddToBasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/AddToBasketFragmentFragmentDoc';
import {
  Button,
  CircularProgress,
  DialogPreset,
  Link,
  Notification,
  useI18n,
} from '@packages/shared';
import { Basket32 } from '@packages/themes/icons';
import { useSession } from '@packages/utilities';
import { getBridge } from '@packages/utilities/src/getBridge';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAuthMutation } from '@packages/gql';
import { AddToBasketDocument } from '@packages/gql/generated/shopping/AddToBasketDocument';
import { MessageFragmentFragmentDoc } from '@packages/gql/generated/shopping/MessageFragmentFragmentDoc';
import { LineItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LineItemFragmentFragmentDoc';
import { selectedQuantity, selectedServices } from '../../atoms';
import { getSkuPart } from '../../helpers/getSkuPart';
import { AddToBasketLayer } from '../AddToBasketLayer';
import { useAddToBasketTracking } from '../Tracking/hooks/useAddToBasketTracking';
import { addToBasketMessages } from './messages';

/* GraphQL */ `
  fragment AddToBasketFragment on Product {
    akl
    sku
    marketcode
    availability {
      isOrderable
      state
    }
    price {
      currency
      value
      valueOld
      saving {
        value
      }
    }
    ...AddToBasketLayerFragment
    ...AddToBasketTrackingFragment
  }
`;

/* GraphQL */ `
  mutation AddToBasket($locale: String!, $sku: String!, $count: Int!, $isSpecialOrder: Boolean!, $serviceLineitems: [AddBasketServiceLineitem!], $trackingParameter: BasketProductTrackingParameter ) {
    addToBasket(
      locale: $locale
      product: {
        sku: $sku
        count: $count
        isSpecialOrder: $isSpecialOrder
        basketOption: []
        serviceLineitems: $serviceLineitems
        trackingParameter: $trackingParameter
      }
      returnAllProducts: false
    ) {
      itemCount
      success
      messages {
        ...MessageFragment
      }
      lineItems {
        ...LineItemFragment
      }
    }
  }
`;

export type AddToBasketButtonProps = {
  /**
   * addToBasket fragment data
   */
  data: FragmentType<typeof AddToBasketFragmentFragmentDoc>;
};

export const AddToBasketButton = ({ data: productData }: AddToBasketButtonProps) => {
  const product = getFragmentData(AddToBasketFragmentFragmentDoc, productData);
  const {
    akl,
    availability: { state, isOrderable },
    sku,
    price,
  } = product;

  const [dialogOpened, setDialogOpened] = useState(false);
  const [openErrorNotification, setOpenErrorNotification] = useState(false);
  const [openWarningNotification, setOpenWarningNotification] = useState(false);
  // these atoms has to be outsourced into a wrapper when AddToBasketButton gets migrated into modules
  // in future this component will also be used from domain order
  const quantity = useAtomValue(selectedQuantity);
  const serviceLineitems = useAtomValue(selectedServices);

  const { formatMessage } = useIntl();
  const { loading, loggedIn } = useSession();
  const { setDefaultProductOutcome } = useAbTesting();

  const isAvailable = state !== 'UNSELECTED' ? isOrderable : true;

  const [{ data, fetching, error }, executeAddToBasket] = useAuthMutation(AddToBasketDocument);
  const { locale } = useI18n();

  const {
    trackLayerClosed,
    trackToBasketClick,
    trackToCheckAndOrder,
    trackAddToCart,
    trackCatalogGAddedProduct,
    trackGlycerinAddToCart,
    getTrackingParameter,
  } = useAddToBasketTracking({
    data: product,
  });

  const isLoading = loading || fetching;

  useEffect(() => {
    if (error) {
      setOpenErrorNotification(true);
    }
    if (data) {
      if (!data.addToBasket.success && data.addToBasket.messages) {
        setOpenErrorNotification(true);
        setDialogOpened(false);
      } else if (data.addToBasket.success) {
        setDefaultProductOutcome(
          [
            {
              count: quantity,
              masterId: akl,
              sku: getSkuPart(sku, 'ArticleNumberAndSize'),
              value: price.value,
            },
          ],
          'ATB',
        );
        trackToCheckAndOrder();
        trackAddToCart();
        trackGlycerinAddToCart();
        trackCatalogGAddedProduct();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, dialogOpened]);

  const onClickHandler = async () => {
    if (state === 'UNSELECTED') {
      setOpenWarningNotification(true);
    } else {
      const result = await executeAddToBasket({
        locale,
        sku,
        count: quantity,
        isSpecialOrder: false,
        serviceLineitems,
        trackingParameter: getTrackingParameter(),
      });

      if (result.error || (result.data && !result.data.addToBasket)) {
        setOpenErrorNotification(true);
      } else if (result.data?.addToBasket) {
        getBridge().updateBasket();
        setDialogOpened(true);
      }
    }
  };

  const trackToCheckAndOrderHandler = () => {
    trackToBasketClick();
  };

  const layerCloseHandler = () => {
    setDialogOpened(false);
    trackLayerClosed();
  };

  return (
    <>
      {openWarningNotification && (
        <Notification
          open={openWarningNotification}
          severity="warning"
          autoHideDuration={5000}
          onClose={() => setOpenWarningNotification(false)}
        >
          {formatMessage(addToBasketMessages.warningMessage)}
        </Notification>
      )}
      {openErrorNotification && (
        <Notification
          open={openErrorNotification}
          severity="error"
          autoHideDuration={5000}
          onClose={() => setOpenErrorNotification(false)}
        >
          {data &&
          !data.addToBasket.success &&
          data.addToBasket.messages &&
          data.addToBasket.messages.length > 0
            ? getFragmentData(MessageFragmentFragmentDoc, data.addToBasket.messages)[0].description
            : formatMessage(addToBasketMessages.errorMessage)}
        </Notification>
      )}
      {dialogOpened && data && data.addToBasket.success && (
        <DialogPreset
          title={formatMessage(addToBasketMessages.headline)}
          maxWidth="lg"
          stickyFooter
          buttons={
            <>
              {loggedIn && (
                <Button
                  LinkComponent={Link}
                  onClick={trackToCheckAndOrderHandler}
                  href="/kasse/zahlung"
                  color="primary"
                  size="large"
                  sx={{ maxWidth: ['100%', '100%', '180px'] }}
                  fullWidth
                >
                  {formatMessage(addToBasketMessages.toCheckout)}
                </Button>
              )}
              <Button
                LinkComponent={Link}
                onClick={trackToCheckAndOrderHandler}
                href="/warenkorb"
                color={loggedIn ? 'inherit' : 'primary'}
                size="large"
                sx={{ maxWidth: ['100%', '100%', '180px'] }}
                fullWidth
              >
                {formatMessage(addToBasketMessages.navigateToCart)}
              </Button>
              <Button
                variant="text"
                size="large"
                onClick={layerCloseHandler}
                sx={{ maxWidth: ['100%', '100%', '180px'] }}
                fullWidth
              >
                {formatMessage(addToBasketMessages.continueShopping)}
              </Button>
            </>
          }
          open={dialogOpened}
          onClose={layerCloseHandler}
        >
          <AddToBasketLayer
            lineItem={getFragmentData(LineItemFragmentFragmentDoc, data.addToBasket.lineItems[0])}
            data={product}
          />
        </DialogPreset>
      )}
      <Button
        endIcon={isLoading ? <CircularProgress color="secondary" size="s" /> : <Basket32 />}
        color="primary"
        size="large"
        onClick={onClickHandler}
        fullWidth
        sx={{ whiteSpace: 'nowrap' }}
        disabled={!isAvailable || isLoading}
      >
        {formatMessage(isAvailable ? addToBasketMessages.available : addToBasketMessages.soldOut)}
      </Button>
    </>
  );
};
